import React, { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Default query options that apply to all queries
const defaultQueryOptions = {
    queries: {
        // Default stale time: 30 seconds
        staleTime: 30 * 1000,
        // Default garbage collection time: 5 minutes (was cacheTime in v4)
        gcTime: 5 * 60 * 1000,
        // Retry failed requests 3 times
        retry: 0,
        // Retry delay that backs off exponentially
        retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
        // Refetch on window focus
        refetchOnWindowFocus: true,
        // Don't refetch on reconnect if data is fresh
        refetchOnReconnect: 'always' as const,
        // Don't refetch on mount if data is fresh
        refetchOnMount: true,
        // Network mode
        networkMode: 'online' as const,
    },
    mutations: {
        // Retry failed mutations once
        retry: 0,
        // Network mode for mutations
        networkMode: 'online' as const,
    },
};

// Create a provider component
interface ReactQueryProviderProps {
    children: React.ReactNode;
}

export function ReactQueryProvider({ children }: ReactQueryProviderProps) {
    // Create the QueryClient inside the component to ensure it's only created once
    const [queryClient] = useState(
        () => new QueryClient({
            defaultOptions: defaultQueryOptions,
        })
    );

    return (
        <QueryClientProvider client={queryClient}>
            {children}
        </QueryClientProvider>
    );
}

export default ReactQueryProvider; 